import type { Locale } from 'ant-design-vue/es/locale';

import type { App } from 'vue';

import type { LocaleSetupOptions, SupportedLanguagesType } from '@vben/locales';

import { ref } from 'vue';

import {
  setupI18n as coreSetup,
  i18n,
  loadLocalesMapFromDir,
} from '@vben/locales';
import { preferences } from '@vben/preferences';

import antdEnLocale from 'ant-design-vue/es/locale/en_US';
import antdDefaultLocale from 'ant-design-vue/es/locale/zh_CN';
import dayjs from 'dayjs';

// 从 i18n 实例创建 $t 和 $te 函数（包含框架层 + 应用层翻译）
const $t = i18n.global.t;
const $te = i18n.global.te;

const antdLocale = ref<Locale>(antdDefaultLocale);

const modules = import.meta.glob('./langs/**/*.json');

const localesMap = loadLocalesMapFromDir(
  /\.\/langs\/([^/]+)\/(.*)\.json$/,
  modules,
);
/**
 * Normalize locale string (e.g., 'zh' -> 'zh-CN', 'en' -> 'en-US')
 * 应用层的 locale 规范化，避免修改框架核心代码
 * @param locale
 */
function normalizeLocale(locale: string): SupportedLanguagesType {
  const localeMap: Record<string, SupportedLanguagesType> = {
    en: 'en-US',
    zh: 'zh-CN',
    'zh-cn': 'zh-CN',
    'en-us': 'en-US',
  };

  const normalized = localeMap[locale.toLowerCase()];
  return normalized || (locale as SupportedLanguagesType);
}

/**
 * 深度合并对象（v3.0）
 */
function deepMerge(target: Record<string, any>, source: Record<string, any>) {
  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key])
    ) {
      if (!target[key]) {
        target[key] = {};
      }
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
}

/**
 * 加载应用特有的语言包（v3.0 - 支持三层架构）
 *
 * v3.0 变更：
 * - 支持子目录（system/*）
 * - 深度合并同命名空间文件
 * - 每个文件自带命名空间（page.*, system.*, {module}.*）
 *
 * @param lang
 */
async function loadMessages(lang: SupportedLanguagesType) {
  // 规范化 locale
  const normalizedLang = normalizeLocale(lang);

  const [appLocaleMessages] = await Promise.all([
    localesMap[normalizedLang]?.(),
    loadThirdPartyMessage(normalizedLang),
  ]);

  // v3.0: 直接深度合并所有文件
  const messages = appLocaleMessages?.default || {};
  const mergedMessages: Record<string, any> = {};

  for (const fileContent of Object.values(messages)) {
    if (fileContent && typeof fileContent === 'object') {
      deepMerge(mergedMessages, fileContent);
    }
  }

  return mergedMessages;
}

/**
 * 加载第三方组件库的语言包
 * @param lang
 */
async function loadThirdPartyMessage(lang: SupportedLanguagesType) {
  await Promise.all([loadAntdLocale(lang), loadDayjsLocale(lang)]);
}

/**
 * 加载dayjs的语言包
 * @param lang
 */
async function loadDayjsLocale(lang: SupportedLanguagesType) {
  let locale;
  switch (lang) {
    case 'en-US': {
      locale = await import('dayjs/locale/en');
      break;
    }
    case 'zh-CN': {
      locale = await import('dayjs/locale/zh-cn');
      break;
    }
    // 默认使用英语
    default: {
      locale = await import('dayjs/locale/en');
    }
  }
  if (locale) {
    dayjs.locale(locale);
  } else {
    console.error(`Failed to load dayjs locale for ${lang}`);
  }
}

/**
 * 加载antd的语言包
 * @param lang
 */
async function loadAntdLocale(lang: SupportedLanguagesType) {
  switch (lang) {
    case 'en-US': {
      antdLocale.value = antdEnLocale;
      break;
    }
    case 'zh-CN': {
      antdLocale.value = antdDefaultLocale;
      break;
    }
  }
}

async function setupI18n(app: App, options: LocaleSetupOptions = {}) {
  // 规范化默认 locale
  const defaultLocale = normalizeLocale(
    options.defaultLocale || preferences.app.locale,
  );

  await coreSetup(app, {
    defaultLocale,
    loadMessages,
    missingWarn: !import.meta.env.PROD,
    ...options,
  });
}

export { $t, $te, antdLocale, setupI18n };
